import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const GAME_DURATION = 60;
const START_COUNTDOWN = 5;

// Balloon images: +1 (friend), -1 (monster)
const GOOD_IMG = require('./images/good.PNG');
const BAD_IMG = require('./images/bad.PNG');

const INFINITY_ICON = (
  <svg fill="#ffe179" width="48px" height="40px" viewBox="0 0 24.00 24.00" style={{verticalAlign:'middle'}} xmlns="http://www.w3.org/2000/svg" stroke="#ffe179" strokeWidth="0.168">
    <path d="M20.288 9.463a4.856 4.856 0 0 0-4.336-2.3 4.586 4.586 0 0 0-3.343 1.767c.071.116.148.226.212.347l.879 1.652.134-.254a2.71 2.71 0 0 1 2.206-1.519 2.845 2.845 0 1 1 0 5.686 2.708 2.708 0 0 1-2.205-1.518L13.131 12l-1.193-2.26a4.709 4.709 0 0 0-3.89-2.581 4.845 4.845 0 1 0 0 9.682 4.586 4.586 0 0 0 3.343-1.767c-.071-.116-.148-.226-.212-.347l-.879-1.656-.134.254a2.71 2.71 0 0 1-2.206 1.519 2.855 2.855 0 0 1-2.559-1.369 2.825 2.825 0 0 1 0-2.946 2.862 2.862 0 0 1 2.442-1.374h.121a2.708 2.708 0 0 1 2.205 1.518l.7 1.327 1.193 2.26a4.709 4.709 0 0 0 3.89 2.581h.209a4.846 4.846 0 0 0 4.127-7.378z"></path>
  </svg>
);

// SFX file imports (webpack via JS import or require)
const GAME_MUSIC = require('./images/game.mp3');
const GOOD_SFX = require('./images/good.mp3');
const BAD_SFX = require('./images/bad.mp3');
const WIN_SFX = require('./images/win.mp3');
const LOSE_SFX = require('./images/lose.mp3');
const PG_SFX = require('./images/pg.mp3');

function getRandomBalloonId() {
  return Math.random().toString(36).slice(2) + Date.now();
}

function getRandomBalloon() {
  // 65% good, 35% bad (from prompt, could tune)
  const kind = Math.random() < 0.65 ? 'good' : 'bad';
  const size = 92 + Math.random()*70; // 62-132px
  const left = 1 + Math.random()*86; // %
  return {
    id: getRandomBalloonId(),
    kind,
    img: kind === 'good' ? GOOD_IMG : BAD_IMG,
    size,
    left,
    top: -size, // start just above screen
    speed: 1.6 + Math.random()*3.2, // how many seconds to fall
    clicked: false
  };
}

export default function BalloonGame() {
  const [phase, setPhase] = useState('ready'); // 'ready', 'countdown', 'playing', 'ended'
  const [score, setScore] = useState(0);
  const [countdown, setCountdown] = useState(START_COUNTDOWN);
  const [timer, setTimer] = useState(GAME_DURATION);
  const [balloons, setBalloons] = useState([]);
  const gameAreaRef = useRef();
  const [specialMsg, setSpecialMsg] = useState(null);
  const [showInfinity, setShowInfinity] = useState(false);
  const [repeatKey, setRepeatKey] = useState(0); // for restart logic
  const [goodQueue, setGoodQueue] = useState(0); // for good.mp3 queueing
  const [goodSegIdx, setGoodSegIdx] = useState(0); // for segment switching: 0→play 1-2s, 1→2-3s, 2→3-4s
  const gameMusicRef = useRef();
  const goodSfxRef = useRef();
  const goodSfxNextRef = useRef(Promise.resolve());
  const [didPlayMusic, setDidPlayMusic] = useState(false); // to guarantee at least one user-initiated play
  const [animScoreMsg, setAnimScoreMsg] = useState(null); // { score, infinity, show } for fade anim
  const pgRef = useRef();
  const navigate = useNavigate();

  // Countdown before playing
  useEffect(() => {
    if (phase === 'ready') {
      setCountdown(START_COUNTDOWN);
      setTimeout(() => setPhase('countdown'), 500);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 'countdown') {
      if (countdown > 0) {
        const t = setTimeout(() => {
          setCountdown((c) => c - 1);
        }, 1000);
        return () => clearTimeout(t);
      } else {
        setPhase('playing');
        setTimer(GAME_DURATION);
      }
    }
  }, [phase, countdown]);

  useEffect(() => {
    if (phase === 'playing') {
      if (timer > 0) {
        const t = setTimeout(() => setTimer((t) => t - 1), 1000);
        return () => clearTimeout(t);
      } else {
        setPhase('ended');
      }
    }
  }, [phase, timer]);

  // Spawn balloon interval
  useEffect(() => {
    if (phase !== 'playing') {
      setBalloons([]);
      return;
    }
    let running = true;
    function spawn() {
      if (!running) return;
      setBalloons((prev) => [...prev, getRandomBalloon()]);
      // every 350–750ms
      const next = 350 + Math.random()*400;
      setTimeout(spawn, next);
    }
    spawn();
    return () => { running = false; };
  }, [phase]);

  // Remove balloons when off-screen
  useEffect(() => {
    if (phase !== 'playing') return;
    const clean = setInterval(() => {
      setBalloons((arr) => arr.filter(b => !b.clicked && b.top < window.innerHeight + b.size + 80));
    }, 1200);
    return () => clearInterval(clean);
  }, [phase]);

  // Animate falling (CSS class, not useEffect on each)

  // Play music ONLY after user events (click balloon or Go), not at phase change only

  // Force play bg music on any rewarding user interaction (Go, first click, etc)
  function ensureGameMusicPlay() {
    if (!gameMusicRef.current) return;
    // Unmute and set volume just in case
    gameMusicRef.current.muted = false;
    gameMusicRef.current.volume = 1;
    if (gameMusicRef.current.paused || gameMusicRef.current.ended) {
      gameMusicRef.current.currentTime = 0;
      gameMusicRef.current.play().then(()=>{
        setDidPlayMusic(true);
      }).catch(e=>{
        // show error or ignore (browser silent)
        // You may optionally alert: alert('Klik sekali di layar agar suara muncul!')
        //console.log('Game music play failed',e);
      });
    }
  }
  // On Go (after countdown), force BGM play
  useEffect(() => {
    if (phase === 'playing' && !didPlayMusic) {
      ensureGameMusicPlay();
    }
  }, [phase, didPlayMusic]);

  // Play win/lose at END
  useEffect(() => {
    if (phase === 'ended') {
      if (score > 32) {
        new Audio(WIN_SFX).play().catch(()=>{});
      } else {
        new Audio(LOSE_SFX).play().catch(()=>{});
      }
    }
  }, [phase]);

  // Play 'good' sfx in a queued way
  function queueGoodSfx() {
    setGoodQueue(q => q+1);
  }
  useEffect(() => {
    if (!goodSfxRef.current) return;
    if (goodQueue === 0) return;
    // chaining via promise so not overlapped
    goodSfxNextRef.current = goodSfxNextRef.current.then(() => {
      return new Promise(resolve => {
        goodSfxRef.current.currentTime = 0;
        goodSfxRef.current.play().catch(()=>{});
        goodSfxRef.current.onended = () => resolve();
      });
    });
    setGoodQueue(q=>q-1);
  }, [goodQueue]);

  // On repeat, reset music logic
  function handleRepeatGame() {
    setScore(0);
    setShowInfinity(false);
    setSpecialMsg(null);
    setPhase('ready');
    setRepeatKey(prev=>prev+1);
    setGoodSegIdx(0);
    setDidPlayMusic(false);
    // HARD STOP and rewind
    if (gameMusicRef.current) {
      gameMusicRef.current.pause();
      gameMusicRef.current.currentTime = 0;
    }
  }

  // Special: Play good.mp3 only 1s-segment: goodSegIdx:[0→1-2s, 1→2-3s, 2→3-4s], looping
  const pendingGoodTimeout = useRef(null);
  function playNextGoodSegment() {
    const seg = [2.1, 4, 2.1][goodSegIdx % 3];
    if (!goodSfxRef.current) return;
    // Cancel prev segment (for rapid click safety)
    try { goodSfxRef.current.pause(); } catch{};
    goodSfxRef.current.currentTime = seg;
    goodSfxRef.current.play().catch(()=>{});
    // force pause after 1s
    if (pendingGoodTimeout.current) clearTimeout(pendingGoodTimeout.current);
    pendingGoodTimeout.current = setTimeout(() => {
      try { goodSfxRef.current.pause(); } catch{};
    }, 950);
  }
  // On good click
  function handleBalloonClick(balloon) {
    if (phase !== 'playing' || balloon.clicked) return;
    ensureGameMusicPlay(); // every click will try activate if missed before
    setBalloons((arr) => arr.map(b => b.id === balloon.id ? { ...b, clicked: true } : b));
    setTimeout(() => setBalloons((arr) => arr.filter(b => b.id !== balloon.id)), 600);
    setScore((s) => s + (balloon.kind === 'good' ? 1 : -1));
    if (balloon.kind === 'good') {
      playNextGoodSegment();
      setGoodSegIdx(i => (i+1)%3);
    } else {
      new Audio(BAD_SFX).play().catch(()=>{});
    }
  }
  // On unmount or repeat, cleanup pendingGoodTimeout
  useEffect(()=>()=>(()=>{if(pendingGoodTimeout.current)clearTimeout(pendingGoodTimeout.current);}),[]);

  // Special message & anim triggered
  useEffect(() => {
    let scoreMsg = null;
    if (score === 20) scoreMsg = {score, infinity: false, msg: 'congrats! ini our anniversary date! semangat!'};
    if (score === 26) scoreMsg = {score, infinity: false, msg: 'ini umur sayang sekarang hehe'};
    if (score > 32 && !showInfinity) scoreMsg = {score, infinity: true, msg: 'infinity pokoknyo. love you sayang!'};

    if(scoreMsg){
      setAnimScoreMsg({...scoreMsg, show:true});
      setSpecialMsg(scoreMsg.msg);
      // Play SFX pg.mp3
      if(pgRef.current){ try { pgRef.current.currentTime = 0; pgRef.current.play().catch(()=>{});} catch{} }
      setTimeout(()=>setAnimScoreMsg(null),1300);
      setTimeout(()=>setSpecialMsg(null), 3000);
      if (score > 32 && !showInfinity) setShowInfinity(true);
    }
  }, [score]);

  return (
    <div key={repeatKey} style={{minHeight:'100vh',position:'relative',overflow:'hidden',background:'#090e13'}}>
      <AnimatedBubblesBackground />
      {/* Timer & Score fixed top, center */}
      <div style={{position:'fixed',top:0,left:0,width:'100%',zIndex:10,display:'flex',flexDirection:'column',alignItems:'center',pointerEvents:'none'}}>
        <div style={{marginTop:14,background:'rgba(24,22,41,0.85)',boxShadow:'0 2px 12px #2224b1a1',padding:'10px 44px',borderRadius:18,minWidth:140,display:'flex',flexDirection:'column',alignItems:'center'}}>
          <div style={{color:'#ffde81',fontWeight:800,fontSize:28,letterSpacing:1,textShadow:'0 2px 16px #403d0d',display:'flex',alignItems:'center',justifyContent:'center',minHeight:40}}>
            {phase === 'countdown' ? '' : timer}
            <span style={{fontSize:16,verticalAlign:'top',marginLeft:8,opacity:0.7}}>{phase === 'countdown' ? '' : 's'}</span>
          </div>
          <div style={{
              marginTop:4,
              color:'#83e9ff',
              fontWeight:800,
              fontSize:20,
              letterSpacing:0.5,
              textShadow:'0 1px 6px #006a9c50',
              minHeight:30,
              display:'flex',alignItems:'center',justifyContent:'center'}}>
            Score: {showInfinity ? INFINITY_ICON : score}
          </div>
        </div>
      </div>
      {/* Overlay & Centered content for READY/COUNTDOWN phase */}
      {phase==='ready' && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100}}>
          <h1 style={{color:'#afd0fb',fontWeight:800,fontSize:'clamp(2.3rem,6vw,3.5rem)', textAlign:'center',letterSpacing:2,textShadow:'0 3px 32px #0d042b60'}}>Balloon Game 🎈</h1>
        </div>
      )}
      {phase==='countdown' && (
        <>
          <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(255,255,255,0.64)',zIndex:100}}>
            <h1 style={{color:'#222',fontWeight:900,fontSize:'clamp(2.9rem,8vw,5rem)', textAlign:'center',letterSpacing:2,textShadow:'0 3px 22px #fff',margin:0}}>{countdown===0? 'Go!' : countdown}</h1>
          </div>
        </>
      )}
      {/* Special Message Overlay */}
      {(animScoreMsg && animScoreMsg.show) && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1050,pointerEvents:'none'}}>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:window.innerWidth<431? '7px':'14px'}}>
            <div style={{
              animation:'fadeInBigScore 1.1s',
              background:'none',
              fontWeight:900,
              color: animScoreMsg.infinity ? '#ffe179' : '#64fbe5',
              fontSize: window.innerWidth<431?'3.6rem':'4.5rem',
              display:'flex',alignItems:'center',justifyContent:'center',
              filter:'drop-shadow(0 6px 26px #0007)',
              textShadow:'0 2px 38px #211b7a80',
            }}>
              {animScoreMsg.infinity ? INFINITY_ICON : animScoreMsg.score}
            </div>
            {/* Special message FADE anim below main score - both centered */}
            {animScoreMsg.msg && (
              <div style={{
                background:'rgba(22,19,44,0.82)',
                padding:window.innerWidth<431?'19px 9px':'24px 22px',
                borderRadius:15,
                color:'#ffe288',
                fontWeight:900,
                fontSize:window.innerWidth<431? '1.11rem':'1.36rem',
                maxWidth:380,
                boxShadow:'0 6px 40px #211b7a55',
                textAlign:'center',
                letterSpacing:'0.012em',
                opacity:1,
                animation:'fadeMsg 0.66s, fadeOutMsg 0.89s 2.3s',
              }}>{animScoreMsg.msg}</div>
            )}
          </div>
          <style>{`@keyframes fadeInBigScore{0%{opacity:0;transform:scale(0.65);}50%{opacity:1;transform:scale(1.2);}85%{opacity:1;transform:scale(1);}100%{opacity:0;transform:scale(1.2);}}
            @keyframes fadeMsg{from{opacity:0;transform:scale(1.16);} to{opacity:1;transform:scale(1);}}
            @keyframes fadeOutMsg{to{opacity:0;}}
          `}</style>
        </div>
      )}
      {/* End Game Popup, pass repeat logic */}
      <EndPopup phase={phase} score={score} showInfinity={showInfinity}
        onNext={() => navigate('/wish')}
        onRepeat={handleRepeatGame} />
      {/* Balloons + area as before*/}
      <div style={{position: 'relative',zIndex:2, width:'100vw', height:'100vh', margin:0, padding:0, display:'flex',flexDirection:'column',alignItems:'center'}} ref={gameAreaRef}>
        {phase === 'ready' && (
          <h1 style={{ color: '#afd0fb', fontWeight: 800, fontSize: 30, textAlign: 'center',letterSpacing:2,textShadow:'0 3px 32px #0d042b60'}}>Balloon Game 🎈</h1>
        )}
        {phase === 'playing' && (
          <div style={{
              width: '100vw',
              height: '100vh',
              maxWidth: 'none',
              position: 'fixed',
              left: 0,
              top: 0,
              overflow: 'hidden',
              margin: 0,
              padding: 0,
              border: 'none',
              borderRadius: 0,
              zIndex: 3,
              background: 'none',
              boxShadow: 'none',
            }}>
            {balloons.map(b => (
              <img
                key={b.id}
                src={b.img}
                alt={b.kind}
                style={{
                  position:'absolute',
                  left:`${b.left}%`,
                  width:`${b.size}px`,
                  top: b.clicked ? undefined : `-${b.size}px`,
                  transition:'top 0.12s',
                  cursor:'pointer',
                  zIndex:10,
                  boxShadow: 'none',
                  border: 'none',
                  opacity: b.clicked ? 0.2 : 0.92,
                  // when clicked, use scale for pop effect
                  transform: b.clicked ? 'scale(1.22) rotate(-12deg)' : undefined,
                  animation: `falltop${b.id} ${b.speed}s linear`,
                  pointerEvents: b.clicked? 'none' : 'auto',
                  background: 'none',
                }}
                onClick={() => handleBalloonClick(b)}
              />
            ))}
            <style>
              {balloons.map(b =>
                `@keyframes falltop${b.id} {
                  from {top: -${b.size}px;}
                  to {top: ${window.innerHeight + b.size + 40}px;}
                }`
              ).join('\n')}
            </style>
          </div>
        )}
        {phase === 'ended' && (
          <div style={{textAlign:'center',color:'#fff'}}>
            <h2>Game Over</h2>
            <div style={{color:'#f8e075',fontSize:28,fontWeight:800}}>{score}</div>
            {/* <div>Tunggu hasil & popup, coming soon...</div> */}
          </div>
        )}
      </div>
      {/* Hidden audio tags for music, queued effect */}
      <audio ref={gameMusicRef} src={GAME_MUSIC} loop autoPlay={false} />
      <audio ref={goodSfxRef} src={GOOD_SFX} autoPlay={false} preload="auto" />
      <audio ref={pgRef} src={PG_SFX} autoPlay={false} preload="auto" />
    </div>
  );
}

function AnimatedBubblesBackground() {
  // Random colored animated bubbles/blobs
  return (
    <div style={{position:'fixed',inset:0,zIndex:0,overflow:'hidden',pointerEvents:'none',background:'#10142b'}}>
      <Bubble color="#6f7bf7" size={120} left="10%" duration={8} delay={0} topStart={"70vh"} />
      <Bubble color="#00c8ff" size={65} left="60%" duration={7} delay={1.5} topStart={"95vh"}/>
      <Bubble color="#ffa7e5" size={80} left="22%" duration={9} delay={0.5} topStart={"60vh"}/>
      <Bubble color="#ffe984" size={55} left="80%" duration={8} delay={2} topStart={"85vh"}/>
      <Bubble color="#98ffa4" size={48} left="45%" duration={10} delay={0} topStart={"77vh"}/>
      <Bubble color="#e8a976" size={75} left="75%" duration={12} delay={4} topStart={"100vh"}/>
      <style>{`
        @keyframes bubblemove {
          to { transform: translateY(-110vh) scale(1.1) rotate(24deg); opacity: 0.87; }
        }
      `}</style>
    </div>
  );
}

function Bubble({color, size, left, duration, delay, topStart}) {
  return (
    <div style={{
      position:'absolute',
      left,
      width:size,
      height:size,
      bottom:undefined,
      top:topStart,
      background: `radial-gradient(circle at 65% 30%, ${color} 76%, #090e13 100%)`,
      borderRadius:'50%',
      opacity:0.16 + Math.random()*0.14,
      filter:'blur(0.5px)',
      animation: `bubblemove ${duration}s linear infinite`,
      animationDelay: `${delay}s`,
    }} />
  );
}

function EndPopup({phase, score, showInfinity, onNext, onRepeat}) {
  const [show, setShow] = useState(false);
  const [timer, setTimer] = useState(5);
  useEffect(() => {
    if(phase==='ended'){
      setShow(true);
      setTimer(5);
      const t = setInterval(()=>{
        setTimer(prev=>{
          if(prev<=1){ clearInterval(t); return 0; }
          return prev-1;
        });
      },1000);
      // optional: auto next
      // setTimeout(()=>onNext(),5000);
    } else {
      setShow(false);
    }
    // eslint-disable-next-line
  },[phase]);

  // Hide popup if repeat triggered
  useEffect(()=>{ if(phase!=='ended') setShow(false); }, [phase]);

  if(!show) return null;
  return (
    <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(13,12,26,0.79)',zIndex:4000}}>
      <div style={{background:'#191743',padding:window.innerWidth<431?'22px 12px':'28px 32px',borderRadius:19,minWidth:window.innerWidth<431?240:320,boxShadow:'0 6px 50px #211b7a55',display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center'}}>
        <div style={{fontWeight:900,fontSize:'1.45rem',color:'#ffe179',marginBottom:2,lineHeight:1.2}}>
          {showInfinity && INFINITY_ICON}
          {!showInfinity && score}
        </div>
        <div style={{color:'#fff',fontWeight:700,fontSize:'1.04rem',padding:'8px 0 12px 0'}}>
          {showInfinity ? 'revi capek ngitung, pokoknyaa good job cintaku!'
            : 'ih sayang, noob!' }
        </div>
        <div style={{display:'flex',gap:9,justifyContent:'center',flexWrap:'wrap',width:'100%',marginBottom:8}}>
          <button
            style={{background:'#83e9ff',color:'#17193e',border:'none',padding:'10px 27px',borderRadius:17,fontWeight:800,fontSize:'1rem',cursor:'pointer'} }
            onClick={onNext}
          >Next</button>
          <button
            style={{background:'#ffe288',color:'#90146e',border:'none',padding:'10px 23px',borderRadius:17,fontWeight:800,fontSize:'1rem',cursor:'pointer'} }
            onClick={onRepeat}
          >Repeat</button>
        </div>
        <div style={{color:'#e9e6fa',fontSize:'0.96rem',opacity:0.72,marginTop:5}}>Auto next in {timer}s</div>
      </div>
    </div>
  );
}
